/**
 * Customer History — standalone activity page.
 *
 * Layout: full-width on mobile, centered max-w-2xl on desktop.
 * Filter pills (All / Earned / Redeemed / Adjusted) + load-more pagination.
 */
import React, { useEffect, useState } from "react";
import { Award, Gift, Edit3, RotateCcw, ChevronDown, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { SkeletonBlock } from "../../components/Skeleton";
import { relativeTime } from "../../lib/tier";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "earned", label: "Earned" },
  { key: "redeemed", label: "Redeemed" },
  { key: "adjusted", label: "Adjusted" },
];

const PAGE_SIZE = 20;

const TYPE_META = {
  earn: { Icon: Award, color: "gold", verb: "Visit" },
  bonus: { Icon: Award, color: "gold", verb: "Welcome bonus" },
  referral_bonus: { Icon: Award, color: "gold", verb: "Referral bonus" },
  birthday_bonus: { Icon: Award, color: "gold", verb: "Birthday gift" },
  redeem: { Icon: Gift, color: "oxblood", verb: "Reward redeemed" },
  admin_adjustment: { Icon: Edit3, color: "espresso", verb: "Adjustment" },
  void: { Icon: RotateCcw, color: "espresso", verb: "Voided transaction" },
};

const Pill = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    aria-pressed={active}
    className={[
      "px-4 py-2 font-body font-semibold text-[11px] tracking-[0.2em] uppercase transition-colors",
      active
        ? "bg-napoli-oxblood text-napoli-cream border border-napoli-oxblood"
        : "bg-napoli-cream-warm text-napoli-espresso border border-napoli-gold/30 hover:border-napoli-oxblood/50",
    ].join(" ")}
  >
    {children}
  </button>
);

const Row = ({ tx }) => {
  const meta = TYPE_META[tx.type] || { Icon: Edit3, color: "espresso", verb: tx.type };
  const positive = (tx.points_delta ?? 0) >= 0;
  const colorClass =
    meta.color === "gold"
      ? "text-napoli-gold"
      : meta.color === "oxblood"
      ? "text-napoli-oxblood"
      : "text-napoli-espresso";
  return (
    <li
      data-testid={`history-tx-${tx.id}`}
      className="bg-napoli-marble border border-napoli-gold/20 px-5 py-4 flex items-center gap-4"
    >
      <div
        aria-hidden
        className="h-10 w-10 shrink-0 flex items-center justify-center bg-napoli-cream-warm border border-napoli-gold/30"
      >
        <meta.Icon size={18} className={colorClass} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-body font-semibold text-[14px] text-napoli-espresso truncate">
          {tx.notes || meta.verb}
        </p>
        <p className="font-body italic text-[12px] text-napoli-espresso-soft">
          {relativeTime(tx.created_at)}
          {tx.amount_spent ? ` · $${tx.amount_spent.toFixed(2)} check` : ""}
        </p>
      </div>
      <p
        className={[
          "font-display font-bold text-[18px] whitespace-nowrap",
          positive ? "text-napoli-gold" : "text-napoli-oxblood",
        ].join(" ")}
      >
        {positive ? "+" : ""}
        {tx.points_delta} pts
      </p>
    </li>
  );
};

const CustomerHistoryPage = () => {
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchPage = async (nextFilter, nextOffset, append) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const { data } = await api.get("/customer/history", {
        params: {
          type: nextFilter,
          limit: PAGE_SIZE,
          offset: nextOffset,
        },
      });
      setItems((prev) => (append ? [...prev, ...(data.transactions || [])] : data.transactions || []));
      setHasMore(!!data.has_more);
      setOffset(nextOffset + (data.transactions || []).length);
    } catch (_) {
      if (!append) setItems([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchPage(filter, 0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  return (
    <section
      data-testid="customer-history-page"
      className="px-6 md:px-10 py-8 md:py-12 max-w-3xl mx-auto"
    >
      <p className="font-body font-semibold text-[10px] tracking-[0.3em] uppercase text-napoli-oxblood">
        Your story
      </p>
      <h1 className="mt-2 font-display font-bold text-[28px] md:text-[36px] text-napoli-espresso leading-tight">
        Your activity at Napoli.
      </h1>
      <p className="mt-2 font-body italic text-[13px] text-napoli-espresso-soft">
        Every visit, every reward, every point.
      </p>

      <div
        data-testid="history-filter-pills"
        className="mt-6 flex flex-wrap gap-2"
      >
        {FILTERS.map((f) => (
          <Pill
            key={f.key}
            active={filter === f.key}
            onClick={() => setFilter(f.key)}
            testId={`history-filter-${f.key}`}
          >
            {f.label}
          </Pill>
        ))}
      </div>

      <div className="mt-6 space-y-3">
        {loading ? (
          <>
            <SkeletonBlock className="h-16" />
            <SkeletonBlock className="h-16" />
            <SkeletonBlock className="h-16" />
          </>
        ) : items.length === 0 ? (
          <div
            data-testid="history-empty"
            className="bg-napoli-cream-warm border border-dashed border-napoli-gold/40 px-6 py-12 text-center"
          >
            <p className="font-flourish text-napoli-oxblood text-3xl">benvenuto</p>
            <p className="mt-2 font-body italic text-[13px] text-napoli-espresso-soft">
              No activity yet. Your story starts on your first visit.
            </p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="history-list">
            {items.map((tx) => (
              <Row key={tx.id} tx={tx} />
            ))}
          </ul>
        )}
      </div>

      {hasMore && !loading ? (
        <div className="mt-6 text-center">
          <button
            type="button"
            data-testid="history-load-more"
            onClick={() => fetchPage(filter, offset, true)}
            disabled={loadingMore}
            className="inline-flex items-center gap-2 border border-napoli-gold/50 text-napoli-espresso font-body font-semibold text-[11px] tracking-[0.22em] uppercase px-6 py-3 hover:bg-napoli-cream-warm disabled:opacity-50"
          >
            {loadingMore ? <Loader2 size={14} className="animate-spin" /> : <ChevronDown size={14} />}
            {loadingMore ? "Loading…" : "Load more"}
          </button>
        </div>
      ) : null}
    </section>
  );
};

export default CustomerHistoryPage;
